import {useSearchParams} from "react-router-dom";
import {useGetMarketplaceListingsQuery} from "../../store/services/publicApi";
import ComingSoon from "./ComingSoon";

type MarketplaceListing = {
    id: string;
    name: string;
    description?: string;
    category?: {id: string; name: string};
    company?: {
        id: string;
        name: string;
        location?: {country?: {name?: string}; region?: {name?: string}};
    };
};

/**
 * Public catalog. Listings come from getMarketplaceListings.
 * An empty catalog still explains that products are added by suppliers.
 * If the query itself fails, the previous coming-soon page is shown.
 */
export default function MarketplacePage() {
    const [params] = useSearchParams();
    const categoryId = params.get("categoryId") || undefined;
    const searchText = params.get("product") || undefined;
    const supplier = params.get("supplier") || undefined;
    const country = params.get("country") || undefined;

    const {data, isLoading, isError} = useGetMarketplaceListingsQuery({
        categoryId,
        searchText,
        supplier,
        country,
    });

    if (isError) {
        return (
            <ComingSoon
                title="Energy Marketplace"
                description="A catalog of solar, electrical, and productive-use equipment from verified suppliers is on its way. For now, explore energy job opportunities below."
                icon="ni ni-cart"
            />
        );
    }

    const listings = (data || []) as MarketplaceListing[];
    const filters = [searchText, supplier, country].filter(Boolean);

    return (
        <section className="oec-section" style={{paddingTop: "7rem", minHeight: "70vh"}}>
            <div className="oec-section-inner">
                <h1 className="oec-section-title">Energy Marketplace</h1>
                <p className="oec-section-subtitle">
                    {filters.length
                        ? `Results for ${filters.join(" · ")}`
                        : "Products and services from verified companies."}
                </p>

                {isLoading && <p className="oec-section-subtitle">Loading listings...</p>}

                {!isLoading && listings.length === 0 && (
                    <p className="oec-section-subtitle" style={{marginTop: "1.5rem"}}>
                        No published products match this search yet. Suppliers add listings from their company account.
                    </p>
                )}

                <div className="oec-card-grid oec-card-grid-roomy" style={{marginTop: "1.25rem"}}>
                    {listings.map((listing) => {
                        const place = [listing.company?.location?.region?.name, listing.company?.location?.country?.name]
                            .filter(Boolean)
                            .join(", ");
                        return (
                            <article key={listing.id} className="oec-company-card">
                                <h2 className="oec-cat-card-title">{listing.name}</h2>
                                <p className="oec-company-type">
                                    <i className="fas fa-industry"/>
                                    <span>{listing.company?.name}</span>
                                </p>
                                {listing.category?.name && (
                                    <p className="oec-company-meta">
                                        <i className="fas fa-tag"/>
                                        <span>{listing.category.name}</span>
                                    </p>
                                )}
                                {place && (
                                    <p className="oec-company-meta">
                                        <i className="fas fa-map-marker-alt"/>
                                        <span>{place}</span>
                                    </p>
                                )}
                                {listing.description && <p className="oec-cat-card-text">{listing.description}</p>}
                            </article>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
